import { useState } from 'react';
import { Button } from './Button';
import { ImportPrepare } from './ImportPrepare';
import { ConfirmRunImportModal } from './ConfirmRunImportModal';
import { ConfirmModal } from './ConfirmModal';
import { RollbackImportModal } from './RollbackImportModal';
import { Card, CardHead } from './Card';
import { Icon } from './Icon';
import { Stat } from './Stat';
import { Status } from './Status';
import { useImport, useRunImport, useCancelImport, useRollbackImport } from '@/hooks/useImports';
import { useAuth } from '@/store/auth';
import { exportImportCsv, type PreviewOutput } from '@/api/imports';
import { fmt, fmtBytes, formatDateTime, sourceLabel } from '@/lib/format';

type Props = {
  id: number;
  onBack: () => void;
};

const STATUS_KINDS: Record<string, 'ok' | 'warn' | 'mute' | 'run'> = {
  SUCCEEDED: 'ok',
  PARTIAL: 'warn',
  FAILED: 'warn',
  RUNNING: 'run',
  PENDING: 'mute',
  CANCELLED: 'mute',
  ROLLING_BACK: 'run',
  ROLLED_BACK: 'mute',
};

const STATUS_LABELS: Record<string, string> = {
  SUCCEEDED: 'Terminé',
  PARTIAL: 'Partiel',
  FAILED: 'Échec',
  RUNNING: 'En cours',
  PENDING: 'En attente',
  CANCELLED: 'Annulé',
  ROLLING_BACK: 'Retrait en cours',
  ROLLED_BACK: 'Retiré',
};

/** États terminaux depuis lesquels un lot peut être retiré du journal. */
const ROLLBACKABLE = new Set(['SUCCEEDED', 'PARTIAL', 'FAILED', 'CANCELLED']);

/**
 * Detail page for a single past import. Auto-polls every 2 s while the
 * row is still RUNNING (hook side effect), then settles on the terminal
 * snapshot. Surface the rejection breakdown out of the JSON `report`
 * column when present.
 */
export function ImportDetail({ id, onBack }: Props) {
  const query = useImport(id);
  const runMutation = useRunImport();
  const cancelMutation = useCancelImport();
  const rollbackMutation = useRollbackImport();
  const isAdmin = useAuth((s) => s.user?.roles.includes('ADMIN') ?? false);
  const [exporting, setExporting] = useState(false);
  // Retirer un lot supprime ce qu'il a écrit : réservé à ADMIN, avec motif.
  const [rollbackOpen, setRollbackOpen] = useState(false);
  // Aperçu réalisé pendant cette visite : il complète `previewedAt`, qui dit
  // ce que le serveur a déjà mémorisé lors d'un passage précédent.
  const [freshPreview, setFreshPreview] = useState<{
    sheet: string;
    ok: boolean;
    preview: PreviewOutput | null;
  } | null>(null);
  // Le lancement écrit dans un journal en ajout seul : il passe par une
  // confirmation explicite, jamais par un simple clic.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Arrêter un import en cours laisse en place ce qui a déjà été écrit :
  // « Arrêter » n'est pas « Annuler », et ça doit être dit avant le clic.
  const [confirmCancel, setConfirmCancel] = useState(false);
  const imp = query.data;

  async function handleExportCsv() {
    if (!imp) return;
    const csvName =
      (imp.originalFilename ?? `import-${imp.id}`).replace(/\.(xlsx|xls)$/i, '') + '.csv';
    setExporting(true);
    try {
      await exportImportCsv(imp.id, csvName);
    } catch {
      /* silencieux : l'échec de l'export ne doit pas casser la page */
    } finally {
      setExporting(false);
    }
  }

  if (query.isLoading) {
    return (
      <div className="fade-in" style={{ padding: 22, color: 'var(--ink-3)', fontWeight: 600 }}>
        Chargement…
      </div>
    );
  }
  if (!imp) {
    return (
      <div className="fade-in">
        <Button variant="ghost" size="sm" icon="arrowleft" onClick={onBack} style={{ marginBottom: 16 }}>
          Retour à l'historique
        </Button>
        <Card pad>Import introuvable.</Card>
      </div>
    );
  }

  // Un import est prêt à partir si un aperçu a réussi — soit à l'instant, soit
  // lors d'une visite précédente, auquel cas le serveur l'a mémorisé avec la
  // feuille retenue.
  const chosenSheet = freshPreview?.sheet ?? imp.sheet ?? '';
  const readyToRun = freshPreview?.ok ?? Boolean(imp.previewedAt);
  const needsPreparation = imp.status === 'PENDING' || imp.status === 'CANCELLED' || imp.status === 'FAILED';

  const causes = parseRejectionCauses(imp.report);
  const durationSec = computeDuration(imp.startedAt, imp.finishedAt);
  const rollback = parseRollbackReport(imp.rollbackReport);

  return (
    <div className="fade-in">
      <ConfirmModal
        opened={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={() => {
          cancelMutation.mutate({ id: imp.id });
          setConfirmCancel(false);
        }}
        title={imp.status === 'RUNNING' ? "Arrêter l'import en cours ?" : "Annuler cet import ?"}
        confirmLabel={imp.status === 'RUNNING' ? "Arrêter l'import" : "Annuler l'import"}
        danger
        loading={cancelMutation.isPending}
        warning={
          imp.status === 'RUNNING' ? (
            <>
              <strong>Les lignes déjà écrites resteront.</strong> Arrêter interrompt le traitement,
              mais n'efface pas ce qui a déjà été ingéré — le journal est en ajout seul. L'import
              sera marqué comme annulé, avec son compteur arrêté là où il en était.
            </>
          ) : undefined
        }
      >
        {imp.status === 'RUNNING'
          ? "Le traitement en cours sera interrompu dès que possible."
          : "L'import passera à l'état annulé. Le fichier téléversé reste disponible : vous pourrez le relancer plus tard."}
      </ConfirmModal>

      <RollbackImportModal
        opened={rollbackOpen}
        onClose={() => {
          setRollbackOpen(false);
          rollbackMutation.reset();
        }}
        filename={imp.originalFilename ?? imp.sourceRef}
        itemsAccepted={imp.itemsAccepted}
        loading={rollbackMutation.isPending}
        error={rollbackMutation.error?.message ?? null}
        onConfirm={({ reason, force }) =>
          rollbackMutation.mutate(
            { id: imp.id, reason, force },
            { onSuccess: () => setRollbackOpen(false) },
          )
        }
      />

      <ConfirmRunImportModal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        running={runMutation.isPending}
        filename={imp.originalFilename ?? imp.sourceRef}
        source={imp.source}
        sheet={chosenSheet}
        preview={freshPreview?.preview ?? null}
        relaunch={imp.status !== 'PENDING'}
        onConfirm={(origin) => {
          runMutation.mutate({ id: imp.id, sheet: chosenSheet || undefined, ...origin });
          setConfirmOpen(false);
        }}
      />

      <Button variant="ghost" size="sm" icon="arrowleft" onClick={onBack} style={{ marginBottom: 16 }}>
        Retour à l'historique
      </Button>

      <Card pad style={{ marginBottom: 18 }}>
        <div className="fiche-head" style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div className="tl-node" style={{ width: 44, height: 44 }}>
            <Icon name="upload" size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, fontSize: 18, fontFamily: 'var(--mono)' }}>
                {imp.originalFilename ?? imp.sourceRef}
              </span>
              <Status kind={STATUS_KINDS[imp.status] ?? 'mute'}>
                {STATUS_LABELS[imp.status] ?? imp.status}
              </Status>
            </div>
            <div className="muted" style={{ fontWeight: 600, fontSize: 13, marginTop: 5 }}>
              {sourceLabel(imp.source)} · démarré {formatDateTime(imp.startedAt)}
              {imp.finishedAt ? ` · terminé ${formatDateTime(imp.finishedAt)}` : ''}
            </div>
          </div>
          <div className="fiche-actions" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {(imp.status === 'PENDING' ||
              imp.status === 'CANCELLED' ||
              imp.status === 'FAILED') && (
              <Button
                variant="primary"
                icon={imp.status === 'PENDING' ? 'play' : 'refresh'}
                // Un import ne se lance pas à l'aveugle : le serveur refuse un
                // lancement sans aperçu, autant que le bouton le dise avant de
                // partir en erreur.
                disabled={runMutation.isPending || !readyToRun}
                title={readyToRun ? undefined : "Lancez d'abord un aperçu"}
                onClick={() => setConfirmOpen(true)}
              >
                {runMutation.isPending
                  ? 'Lancement…'
                  : imp.status === 'PENDING'
                    ? "Lancer l'import"
                    : "Relancer l'import"}
              </Button>
            )}
            {(imp.status === 'PENDING' || imp.status === 'RUNNING') && (
              <Button
                variant="ghost"
                icon="x"
                disabled={cancelMutation.isPending}
                onClick={() => setConfirmCancel(true)}
              >
                {cancelMutation.isPending ? 'Arrêt…' : 'Arrêter'}
              </Button>
            )}
            {/\.(xlsx|xls)$/i.test(imp.originalFilename ?? '') && (
              <Button icon="export" disabled={exporting} onClick={handleExportCsv}>
                {exporting ? 'Export…' : 'Exporter en CSV'}
              </Button>
            )}
            {imp.itemsTotal > 0 && <Button icon="download">Rapport CSV</Button>}
            {isAdmin && ROLLBACKABLE.has(imp.status) && (
              <Button
                variant="ghost"
                icon="trash"
                disabled={rollbackMutation.isPending}
                title="Supprimer du journal tout ce que ce lot a écrit"
                onClick={() => setRollbackOpen(true)}
              >
                Retirer le lot
              </Button>
            )}
            {imp.itemsRejected > 0 && (
              <Button variant="primary" icon="refresh">
                Rejouer les rejets
              </Button>
            )}
          </div>
        </div>
      </Card>

      {needsPreparation && (
        <Card pad style={{ marginBottom: 18 }}>
          <CardHead title="Préparation" subtitle="À faire avant de lancer l'import." />
          <ImportPrepare
            importId={imp.id}
            initialSheet={imp.sheet}
            onPreview={({ sheet, preview }) =>
              setFreshPreview({ sheet, ok: preview != null, preview })
            }
          >
            {(preview, loading) =>
              preview && !loading ? (
                <div className="prepare-row">
                  <span className="meta acc">
                    <span className="sq" />
                    {preview.accepted}/{preview.sampleSize} lignes simulées sans rejet
                    {preview.rejected > 0 ? ` · ${preview.rejected} rejet(s)` : ''}
                  </span>
                </div>
              ) : null
            }
          </ImportPrepare>
        </Card>
      )}

      {imp.status === 'ROLLING_BACK' && (
        <Card pad style={{ marginBottom: 18 }}>
          <span className="meta"><span className="sq" />Retrait en cours</span>
          <div style={{ fontWeight: 600, marginTop: 8 }}>
            Les événements du lot sont supprimés et les fiches concernées recalculées. Cette page se
            met à jour toute seule.
          </div>
        </Card>
      )}

      {imp.status === 'ROLLED_BACK' && (
        <Card pad style={{ marginBottom: 18 }}>
          <CardHead
            title="Lot retiré du journal"
            subtitle={`${imp.rolledBackAt ? formatDateTime(imp.rolledBackAt) : ''}${imp.rolledBackBy ? ` · par ${imp.rolledBackBy}` : ''}`}
          />
          <div style={{ padding: '0 22px 16px' }}>
            {imp.rollbackReason && (
              <div style={{ fontSize: 13.5, marginBottom: 14 }}>
                <span className="muted" style={{ fontWeight: 600 }}>Motif — </span>
                {imp.rollbackReason}
              </div>
            )}
            {rollback && (
              <div className="grid grid-4">
                <Stat label="Événements supprimés" value={fmt(rollback.eventsDeleted)} />
                <Stat label="Fiches recalculées" value={fmt(rollback.dossiersReprojected)} />
                <Stat label="Fiches retirées" value={fmt(rollback.dossiersDeleted)} />
                <Stat label="Rejets effacés" value={fmt(rollback.dlqDeleted)} />
              </div>
            )}
            <div className="muted" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 14 }}>
              {rollback?.mode === 'WINDOW'
                ? 'Périmètre reconstitué par fenêtre de temps, système source et acteur (lot antérieur à la traçabilité).'
                : 'Périmètre exact : chaque événement du lot portait son identifiant.'}
              {rollback?.forced ? ' Retrait forcé malgré une autre activité de la même source dans la fenêtre.' : ''}
              {' '}Une sauvegarde antérieure au retrait contient encore ces événements.
            </div>
          </div>
        </Card>
      )}

      {imp.errorMessage && (
        <Card pad style={{ marginBottom: 18, borderColor: 'var(--alert-tint-2)', background: 'var(--alert-tint)' }}>
          <span className="meta alert"><span className="sq" />Erreur de l'import</span>
          <div style={{ fontWeight: 600, marginTop: 8 }}>{imp.errorMessage}</div>
        </Card>
      )}

      <div className="grid grid-4" style={{ marginBottom: 18 }}>
        <Stat label="Total" value={fmt(imp.itemsTotal)} />
        <Stat label="Acceptés" value={fmt(imp.itemsAccepted)} trend="up" />
        <Stat label="Rejetés" value={fmt(imp.itemsRejected)} trend={imp.itemsRejected > 0 ? 'down' : undefined} />
        <Stat label="Durée" value={durationSec != null ? formatDuration(durationSec) : 'en cours'} />
      </div>

      <div className="grid grid-2">
        <Card>
          <CardHead title="Causes de rejet" />
          <div style={{ padding: '16px 22px' }}>
            {causes.length === 0 ? (
              <div className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
                Aucun rejet enregistré pour cet import.
              </div>
            ) : (
              causes.map(({ code, count }) => (
                <div
                  key={code}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}
                >
                  <span className="meta alert"><span className="sq" />{code}</span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>
                    {fmt(count)} ligne{count > 1 ? 's' : ''}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card>
          <CardHead title="Paramètres de l'import" />
          <div style={{ padding: '6px 22px 14px' }}>
            <dl className="kv">
              <dt>Identifiant</dt>
              <dd style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{imp.id}</dd>
              <dt>Référence source</dt>
              <dd style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{imp.sourceRef}</dd>
              <dt>Source</dt>
              <dd>{sourceLabel(imp.source)}</dd>
              <dt>Opérateur</dt>
              <dd style={{ fontFamily: 'var(--mono)' }}>{imp.triggeredBy ?? '—'}</dd>
              <dt>Taille fichier</dt>
              <dd>{fmtBytes(imp.fileSizeBytes)}</dd>
              <dt>Lots</dt>
              <dd>500 par requête</dd>
            </dl>
          </div>
        </Card>
      </div>
    </div>
  );
}

function parseRejectionCauses(report: unknown): Array<{ code: string; count: number }> {
  if (!report || typeof report !== 'object') return [];
  const r = report as Record<string, unknown>;
  const candidates =
    (r.rejectionReasons as Record<string, number> | undefined) ??
    (r.causes as Record<string, number> | undefined);
  if (!candidates) return [];
  return Object.entries(candidates)
    .map(([code, count]) => ({ code, count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count);
}

type RollbackReport = {
  mode: string;
  forced: boolean;
  eventsDeleted: number;
  dossiersReprojected: number;
  dossiersDeleted: number;
  dlqDeleted: number;
};

function parseRollbackReport(report: unknown): RollbackReport | null {
  if (!report || typeof report !== 'object') return null;
  const r = report as Record<string, unknown>;
  const num = (k: string) => Number(r[k]) || 0;
  return {
    mode: String(r.mode ?? ''),
    forced: Boolean(r.forced),
    eventsDeleted: num('eventsDeleted'),
    dossiersReprojected: num('dossiersReprojected'),
    dossiersDeleted: num('dossiersDeleted'),
    dlqDeleted: num('dlqDeleted'),
  };
}

function computeDuration(start: string, end: string | undefined): number | null {
  if (!end) return null;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (Number.isNaN(s) || Number.isNaN(e)) return null;
  return Math.max(0, Math.round((e - s) / 1000));
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m} min ${s.toString().padStart(2, '0')} s`;
}
